import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../supabase/client';
import PasswordStrengthIndicator from '../components/Auth/PasswordStrengthIndicator';
import { 
  validateEmailFormat, 
  suggestEmailCorrection, 
  isDisposableEmail 
} from '../utils/emailValidation';
import { trackAuth } from '../utils/analytics';
import { 
  getPasswordError 
} from '../utils/passwordValidation';
import { useMobileDetection } from '../hooks/useMobileDetection';
import LogoNav from '../components/Navigation/LogoNav';
import './LoginPage.css';

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isMobile, isTablet } = useMobileDetection();
  
  // Treat tablets as mobile for layout purposes
  const useMobileLayout = isMobile || isTablet;
  
  // Check if user is already signed in
  const { user } = useAuth();
  useEffect(() => {
    if (user) {
      // Redirect to intended destination or home
      const from = location.state?.from?.pathname || '/';
      navigate(from, { replace: true });
    }
  }, [user, navigate, location]);

  // Check URL parameters to determine initial mode
  const urlParams = new URLSearchParams(location.search);
  const initialMode = urlParams.get('mode') === 'signup';
  const [isSignUp, setIsSignUp] = useState(initialMode);

  // Clean up URL parameter after setting initial state
  useEffect(() => {
    if (urlParams.get('mode')) {
      const newUrl = window.location.pathname;
      window.history.replaceState({}, document.title, newUrl);
    }
  }, []); // Run once on mount
  const [isOTPMode, setIsOTPMode] = useState(false);
  const [isForgotPasswordMode, setIsForgotPasswordMode] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [localSuccessMessage, setLocalSuccessMessage] = useState<string | null>(null);
  const [showForgotPasswordAfterError, setShowForgotPasswordAfterError] = useState(false);
  
  // Enhanced validation states
  const [emailValidation, setEmailValidation] = useState<{
    isValid: boolean;
    error: string | null;
    suggestion: string | null;
    hasBeenBlurred: boolean;
  }>({
    isValid: false,
    error: null,
    suggestion: null,
    hasBeenBlurred: false
  });

  const [showPasswordStrength, setShowPasswordStrength] = useState(false);
  
  const { signIn, signUp, signInWithGoogle, signInWithOTP, verifyOTP, resetPassword, loading, error, clearMessages } = useAuth();
  const emailInputRef = useRef<HTMLInputElement>(null);

  // Focus email input when page loads
  useEffect(() => {
    const timer = setTimeout(() => {
      emailInputRef.current?.focus();
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  // Handle URL parameters for mode switching
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const mode = urlParams.get('mode');
    
    if (mode === 'forgot-password') {
      setIsForgotPasswordMode(true);
      setIsSignUp(false);
      setIsOTPMode(false);
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [location]);

  // Handle auth callbacks (email confirmations, etc.)
  useEffect(() => {
    const handleAuthCallback = async () => {
      const urlParams = new URLSearchParams(location.search);
      const error = urlParams.get('error');
      const errorDescription = urlParams.get('error_description');
      const errorCode = urlParams.get('error_code');
      const type = urlParams.get('type');
      const code = urlParams.get('code'); // PKCE flow parameter

      console.log('🔍 Auth callback parameters:', { error, errorCode, errorDescription, type, code: code ? `${code.substring(0, 20)}...` : 'None' });

      if (error) {
        console.log('❌ Auth callback error:', { error, errorCode, errorDescription });
        
        let friendlyMessage = 'Authentication error occurred. Please try again.';
        
        if (error === 'duplicate_account') {
          friendlyMessage = 'This email is already registered with a different sign-in method. Please use Email/Password to sign in.';
        } else if (error === 'auth_failed') {
          friendlyMessage = 'Sign-in was not successful. This might be because the email is already registered with a different sign-in method. Please try using Email/Password if you registered that way.';
        } else if (errorCode === 'otp_expired' || errorDescription?.includes('expired')) {
          friendlyMessage = 'Email confirmation link has expired. Please sign up again to receive a new confirmation email.';
        } else if (error === 'access_denied') {
          friendlyMessage = 'Access denied. Please try again.';
        } else if (errorDescription) {
          friendlyMessage = errorDescription;
        }
        
        setFormError(friendlyMessage);
        // Clean up URL
        window.history.replaceState({}, document.title, window.location.pathname);
        return;
      }

      // Handle both legacy (type parameter) and modern (code parameter) email confirmation flows
      if ((type === 'signup' || type === 'email') || code) {
        console.log('✅ Email confirmation detected (type or code parameter), processing...');
        
        // For PKCE flow with code parameter, exchange code for session
        if (code && !type) {
          try {
            console.log('🔄 LoginPage - Exchanging code for session (PKCE flow)...');
            console.log('📱 Device info:', {
              userAgent: navigator.userAgent,
              isMobile: /Mobi|Android/i.test(navigator.userAgent),
              platform: navigator.platform,
              cookieEnabled: navigator.cookieEnabled
            });
            
            const isMobile = /Mobi|Android/i.test(navigator.userAgent);
            
            // 🔧 Mobile-specific: Silent storage check for debugging
            if (isMobile) {
              console.log('📱 Mobile browser detected');
              try {
                const testKey = 'supabase-auth-test';
                localStorage.setItem(testKey, 'test');
                localStorage.removeItem(testKey);
                console.log('📱 localStorage available');
              } catch (storageError) {
                console.warn('📱 localStorage not available, PKCE may fail:', storageError);
                // Continue anyway - let PKCE exchange handle the error
              }
            }
            
            const { data, error: exchangeError } = await supabase.auth.exchangeCodeForSession(code);
            
            console.log('🔍 Code exchange result:', { 
              hasData: !!data, 
              hasSession: !!data?.session, 
              hasUser: !!data?.user,
              error: exchangeError 
            });
            
            if (exchangeError) {
              console.log('❌ Code exchange failed:', exchangeError);
              
              // 🔧 Mobile-specific error handling
              if (isMobile && (
                exchangeError.message?.includes('code') || 
                exchangeError.message?.includes('verifier') ||
                exchangeError.message?.includes('session')
              )) {
                console.log('📱 Mobile PKCE failure detected - but email is confirmed');
                setLocalSuccessMessage('Email confirmed! Please sign in with your email and password.');
              } else {
              setFormError('Email confirmation failed. Please try signing in manually or request a new confirmation email.');
              }
            } else if (data?.user) {
              // Check if user exists (confirmation worked) regardless of immediate session
              console.log('✅ PKCE email confirmation successful for user:', data.user.email);
              setLocalSuccessMessage('Email confirmed successfully! Welcome to ZoningPal!');
              
              // Redirect to appropriate page after brief delay
              setTimeout(() => {
                // Check if user was trying to purchase something
                const pendingPurchase = sessionStorage.getItem('pendingPurchase');
                if (pendingPurchase) {
                  // Don't clear it yet - let pricing page handle the purchase
                  navigate('/pricing', { replace: true });
                } else {
                  navigate('/', { replace: true });
                }
              }, 2000);
            } else if (data) {
              // Code exchange succeeded but user/session structure is unexpected
              console.log('⚠️ Code exchange completed but user data structure is unexpected:', data);
              setLocalSuccessMessage('Email confirmed successfully! You can now sign in with your credentials.');
            } else {
              console.log('❌ Code exchange returned no data');
              if (isMobile) {
                setLocalSuccessMessage('Email confirmed! Please sign in with your email and password.');
              } else {
                setFormError('Email confirmation failed. Please try signing in manually.');
              }
            }
          } catch (err: any) {
            console.error('❌ Unexpected error during code exchange:', err);
            console.log('📱 Mobile debug - Error details:', {
              name: err.name,
              message: err.message,
              stack: err.stack?.substring(0, 200),
              isMobile: /Mobi|Android/i.test(navigator.userAgent)
            });
            
            // More lenient error handling - confirmation might have still worked
            const isMobile = /Mobi|Android/i.test(navigator.userAgent);
            if (isMobile) {
              console.log('📱 Mobile browser detected - using mobile-friendly message');
              if (err.name === 'TypeError' || err.message?.includes('localStorage') || err.message?.includes('storage')) {
                setLocalSuccessMessage('Email confirmed! Please sign in with your email and password.');
              } else {
                setLocalSuccessMessage('Email confirmed! Please sign in now.');
              }
            } else {
              console.log('🔄 Despite error, attempting to check if user can sign in...');
              setLocalSuccessMessage('Email confirmation processed. If you encounter issues, please try signing in manually.');
            }
          }
        } else {
          // Legacy flow - just check for existing session
          console.log('🔄 Legacy email confirmation flow - checking for active session...');
          const { data: { session }, error: sessionError } = await supabase.auth.getSession();
          
          if (sessionError) {
            console.log('❌ Session check error:', sessionError);
            setFormError('Email confirmed but unable to sign you in. Please try signing in manually.');
          } else if (session && session.user) {
            console.log('✅ User signed in after email confirmation:', session.user.email);
            setLocalSuccessMessage('Email confirmed successfully! Welcome to ZoningPal!');
            
            // Redirect to appropriate page after brief delay
            setTimeout(() => {
              // Check if user was trying to purchase something
              const pendingPurchase = sessionStorage.getItem('pendingPurchase');
              if (pendingPurchase) {
                // Don't clear it yet - let pricing page handle the purchase
                navigate('/pricing', { replace: true });
              } else {
                navigate('/', { replace: true });
              }
            }, 2000);
          } else {
            console.log('⚠️ Email confirmed but no active session - user needs to sign in manually');
            setLocalSuccessMessage('Email confirmed successfully! You can now sign in with your credentials.');
          }
        }
        
        // Clean up URL
        window.history.replaceState({}, document.title, window.location.pathname);
      }
    };

    handleAuthCallback();
  }, [location, navigate]);

  // Clear messages when switching between sign in/up
  useEffect(() => {
    setFormError(null);
    setLocalSuccessMessage(null);
    clearMessages();
  }, [isSignUp, clearMessages]);

  // Enhanced email validation with real-time feedback
  useEffect(() => {
    if (!email) {
      setEmailValidation({
        isValid: false,
        error: null,
        suggestion: null,
        hasBeenBlurred: emailValidation.hasBeenBlurred
      });
      return;
    }

    const validation = validateEmailFormat(email);
    const suggestion = suggestEmailCorrection(email);
    
    setEmailValidation({
      isValid: validation.isValid,
      error: validation.isValid ? null : validation.error,
      suggestion: suggestion,
      hasBeenBlurred: emailValidation.hasBeenBlurred
    });
  }, [email, emailValidation.hasBeenBlurred]);

  const handleEmailBlur = () => {
    setEmailValidation(prev => ({ ...prev, hasBeenBlurred: true }));
  };

  const toggleMode = () => {
    setIsSignUp(!isSignUp);
    setIsForgotPasswordMode(false);
    setPassword('');
    setConfirmPassword('');
    setShowPasswordStrength(false);
    setFormError(null);
    setLocalSuccessMessage(null);
    setShowForgotPasswordAfterError(false);
    clearMessages();
  };

  const toggleForgotPasswordMode = () => {
    setIsForgotPasswordMode(!isForgotPasswordMode);
    setIsSignUp(false);
    setPassword('');
    setConfirmPassword('');
    setShowPasswordStrength(false);
    setFormError(null);
    setLocalSuccessMessage(null);
    setShowForgotPasswordAfterError(false);
    clearMessages();
  };

  const validateForm = () => {
    // Email validation
    if (!email) return 'Please enter your email address';
    
    const emailValidation = validateEmailFormat(email);
    if (!emailValidation.isValid) {
      return emailValidation.error || 'Please enter a valid email address';
    }

    if (isDisposableEmail(email)) {
      return 'Please use a permanent email address (disposable emails are not allowed)';
    }

    // Password validation
    if (isSignUp) {
      // Stricter validation for sign up
      if (!password) return 'Please create a password';
      
      const passwordError = getPasswordError(password);
      if (passwordError) return passwordError;
      
      if (password !== confirmPassword) {
        return 'Passwords do not match';
      }
    } else {
      // More lenient validation for sign in
      if (password.length < 1) return 'Please enter your password';
    }
    
    return null;
  };

  const handleEmailSuggestionClick = () => {
    if (emailValidation.suggestion) {
      setEmail(emailValidation.suggestion);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Prevent multiple submissions immediately
    if (isSubmitting || loading) {
      return;
    }
    
    setIsSubmitting(true);
    
    // Clear all messages before submitting
    setFormError(null);
    setLocalSuccessMessage(null);
    clearMessages();
    
    const validationError = validateForm();
    if (validationError) {
      setFormError(validationError);
      setIsSubmitting(false);
      return;
    }
    
    try {
      let result;
      if (isSignUp) {
        result = await signUp(email, password);
      } else {
        result = await signIn(email, password);
      }
      
      if (result.success) {
        setLocalSuccessMessage(result.message || (isSignUp ? 'Account created successfully!' : 'Welcome back!'));
        
        // Track authentication event
        trackAuth(isSignUp ? 'signup' : 'login');
        
        // If sign in successful, redirect after brief delay
        if (!isSignUp && result.success) {
          setTimeout(() => {
            // Check if user was trying to purchase something
            const pendingPurchase = sessionStorage.getItem('pendingPurchase');
            if (pendingPurchase) {
              // Don't clear it yet - let pricing page handle the purchase
              navigate('/pricing', { replace: true });
            } else {
              const from = location.state?.from?.pathname || '/';
              navigate(from, { replace: true });
            }
          }, 1500);
        }
      } else {
        // Handle specific case where account already exists during sign-up
        if (isSignUp && result.message && result.message.includes('already registered')) {
          setFormError('An account with this email already exists. Would you like to sign in instead?');
        } else {
          setFormError(result.message || 'Authentication failed. Please try again.');
          
          // Show forgot password option for incorrect password during sign in
          if (!isSignUp && result.message && result.message.includes('Incorrect email or password')) {
            setShowForgotPasswordAfterError(true);
          }
        }
      }
    } catch (error: any) {
      setFormError(error.message || 'An unexpected error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGoogleSignIn = async () => {
    if (isSubmitting || loading) {
      return;
    }

    setIsSubmitting(true);
    setFormError(null);
    setLocalSuccessMessage(null);
    clearMessages();

    try {
      const result = await signInWithGoogle();
      if (result.success) {
        setLocalSuccessMessage(result.message || 'Redirecting to Google...');
        // Track Google OAuth authentication
        trackAuth('login');
        // Google OAuth will redirect, so we don't need to handle navigation manually
      } else {
        setFormError(result.message || 'Google sign-in failed. Please try again.');
      }
    } catch (error: any) {
      setFormError('Google sign-in failed. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOTPSignIn = async () => {
    if (isSubmitting || loading) {
      return;
    }

    // Validate email first
    const emailValidation = validateEmailFormat(email);
    if (!email || !emailValidation.isValid) {
      setFormError(emailValidation.error || 'Please enter a valid email address');
      return;
    }

    setIsSubmitting(true);
    setFormError(null);
    setLocalSuccessMessage(null);
    clearMessages();

    try {
      const result = await signInWithOTP(email);
      if (result.success) {
        setLocalSuccessMessage(result.message || 'Verification code sent!');
        setIsOTPMode(true);
        // Focus OTP input after brief delay
        setTimeout(() => {
          const otpInput = document.getElementById('otpCode');
          otpInput?.focus();
        }, 300);
      } else {
        setFormError(result.message || 'Failed to send verification code. Please try again.');
      }
    } catch (error: any) {
      setFormError('Failed to send verification code. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOTPVerification = async () => {
    if (isSubmitting || loading) {
      return;
    }

    if (!otpCode || otpCode.length !== 6) {
      setFormError('Please enter the 6-digit verification code');
      return;
    }

    setIsSubmitting(true);
    setFormError(null);
    setLocalSuccessMessage(null);
    clearMessages();

    try {
      const result = await verifyOTP(email, otpCode);
      if (result.success) {
        setLocalSuccessMessage(result.message || 'Welcome!');
        // Track OTP authentication
        trackAuth('login');
        // Redirect after brief delay
        setTimeout(() => {
          // Check if user was trying to purchase something
          const pendingPurchase = sessionStorage.getItem('pendingPurchase');
          if (pendingPurchase) {
            // Don't clear it yet - let pricing page handle the purchase
            navigate('/pricing', { replace: true });
          } else {
            const from = location.state?.from?.pathname || '/';
            navigate(from, { replace: true });
          }
        }, 1500);
      } else {
        setFormError(result.message || 'Invalid verification code. Please try again.');
      }
    } catch (error: any) {
      setFormError('Verification failed. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBackToEmail = () => {
    setIsOTPMode(false);
    setOtpCode('');
    setFormError(null);
    setLocalSuccessMessage(null);
    clearMessages();
  };

  const handleForgotPassword = async () => {
    if (isSubmitting || loading) {
      return;
    }

    // Validate email first
    const emailValidation = validateEmailFormat(email);
    if (!email || !emailValidation.isValid) {
      setFormError(emailValidation.error || 'Please enter a valid email address');
      return;
    }

    setIsSubmitting(true);
    setFormError(null);
    setLocalSuccessMessage(null);
    clearMessages();

    try {
      const result = await resetPassword(email);
      if (result.success) {
        setLocalSuccessMessage(result.message || 'Password reset instructions sent!');
        // Stay on forgot password screen to show success message
      } else {
        setFormError(result.message || 'Failed to send password reset email. Please try again.');
      }
    } catch (error: any) {
      setFormError('Failed to send password reset email. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Determine input styling based on validation state
  const getEmailInputClass = () => {
    const baseClass = 'auth-input';
    
    // Don't show validation styling until user has blurred the field or tried to submit
    if (!emailValidation.hasBeenBlurred && !formError) {
      return baseClass;
    }
    
    if (email && emailValidation.isValid) {
      return `${baseClass} valid`;
    } else if (email && !emailValidation.isValid) {
      return `${baseClass} error`;
    }
    
    return baseClass;
  };

  const shouldShowEmailError = () => {
    return emailValidation.hasBeenBlurred && email && !emailValidation.isValid;
  };

  const displayError = formError || error;
  const displaySuccess = localSuccessMessage;

  return (
    <div className="login-page">
      {!useMobileLayout && <LogoNav />}
      <div className="login-container">
        {/* Login Card */}
        <div className="login-card">
          <div className="login-header">
            <h1>
              {isForgotPasswordMode 
                ? 'Reset Your Password'
                : isOTPMode 
                  ? 'Enter verification code' 
                  : isSignUp 
                    ? 'Create Your Account' 
                    : 'Welcome to ZoningPal'}
            </h1>
            <p className="login-subtitle">
              {isForgotPasswordMode
                ? 'Enter your email address and we\'ll send you password reset instructions'
                : isOTPMode 
                  ? `We've sent a 6-digit code to ${email}`
                  : isSignUp 
                    ? 'Sign up to access premium zoning analysis features' 
                    : 'Log in or sign up'}
            </p>
          </div>

          {/* Only show OAuth/OTP options when not in forgot password mode */}
          {!isForgotPasswordMode && (
            <>
              {/* Google Sign-In Button */}
              <button 
                type="button"
                onClick={handleGoogleSignIn}
                disabled={isSubmitting || loading}
                className={`google-signin-button ${isSubmitting || loading ? 'loading' : ''}`}
              >
                <svg 
                  className="google-icon" 
                  viewBox="0 0 24 24" 
                  width="20" 
                  height="20"
                >
                  <path 
                    fill="#4285F4" 
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path 
                    fill="#34A853" 
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path 
                    fill="#FBBC05" 
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path 
                    fill="#EA4335" 
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
                {isSubmitting || loading ? 'Connecting...' : 'Continue with Google'}
              </button>

              <div className="auth-divider">
                <span>or</span>
              </div>

              {/* Continue with Email (OTP) Button */}
              <button 
                type="button"
                onClick={handleOTPSignIn}
                disabled={isSubmitting || loading || isOTPMode}
                className={`email-continue-button ${isSubmitting || loading ? 'loading' : ''}`}
              >
                {isSubmitting || loading ? 'Sending code...' : 'Continue with email'}
              </button>

              {!isOTPMode && (
                <div className="auth-divider">
                  <span>or sign in with password</span>
                </div>
              )}
            </>
          )}
          
          {/* Success Message */}
          {displaySuccess && (
            <div className="auth-success" role="alert">
              <div className="success-icon">✓</div>
              <div className="success-text">{displaySuccess}</div>
            </div>
          )}


          {/* Error Message */}
          {displayError && (
            <div className="auth-error" role="alert">
              <div className="error-icon">!</div>
              <div className="error-text">{displayError}</div>
              {/* Show forgot password option after wrong password error */}
              {showForgotPasswordAfterError && !isSignUp && !isForgotPasswordMode && (
                <div className="forgot-password-suggestion">
                  <button 
                    type="button" 
                    onClick={toggleForgotPasswordMode}
                    className="forgot-password-link"
                  >
                    Forgot your password?
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Forgot Password Interface */}
          {isForgotPasswordMode && (
            <div className="forgot-password-form">
              <div className="form-group">
                <label htmlFor="email">Email Address</label>
                <input
                  ref={emailInputRef}
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onBlur={handleEmailBlur}
                  required
                  placeholder="Enter your email address"
                  className={getEmailInputClass()}
                  disabled={isSubmitting || loading}
                  autoComplete="email"
                />
                
                {/* Email validation feedback */}
                {email && (
                  <div className="email-validation-feedback">
                    {shouldShowEmailError() && (
                      <div className="field-error">{emailValidation.error || 'Please enter a valid email address'}</div>
                    )}
                    
                    {emailValidation.suggestion && (
                      <div className="email-suggestion">
                        Did you mean{' '}
                        <button 
                          type="button"
                          className="suggestion-link"
                          onClick={handleEmailSuggestionClick}
                        >
                          {emailValidation.suggestion}
                        </button>?
                      </div>
                    )}
                  </div>
                )}
              </div>
              
              <button 
                type="button"
                onClick={handleForgotPassword}
                disabled={isSubmitting || loading || !email || !emailValidation.isValid}
                className={`auth-submit-button ${isSubmitting || loading ? 'loading' : ''}`}
              >
                {isSubmitting || loading ? 'Sending Instructions...' : 'Send Reset Instructions'}
              </button>
              
              <div className="back-to-signin">
                <button 
                  type="button"
                  onClick={toggleForgotPasswordMode}
                  className="back-link"
                >
                  ← Back to Sign In
                </button>
              </div>
            </div>
          )}

          {/* OTP Verification Interface */}
          {isOTPMode && (
            <div className="otp-verification">
              <div className="otp-input-container">
                <input
                  id="otpCode"
                  type="text"
                  value={otpCode}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                    setOtpCode(value);
                  }}
                  placeholder="6-digit code"
                  className="otp-input"
                  disabled={isSubmitting || loading}
                  maxLength={6}
                  autoComplete="one-time-code"
                />
              </div>
              
              <button 
                type="button"
                onClick={handleOTPVerification}
                disabled={isSubmitting || loading || otpCode.length !== 6}
                className={`otp-verify-button ${isSubmitting || loading ? 'loading' : ''}`}
              >
                {isSubmitting || loading ? 'Verifying...' : 'Sign up'}
              </button>
              
              <div className="otp-resend">
                <button 
                  type="button"
                  onClick={handleBackToEmail}
                  className="resend-button"
                >
                  Change
                </button>
                <span> • </span>
                <button 
                  type="button"
                  onClick={handleOTPSignIn}
                  disabled={isSubmitting || loading}
                  className="resend-button"
                >
                  Resend code
                </button>
              </div>
            </div>
          )}
                     
          {/* Password Form - Hidden during forgot password mode */}
          {!isForgotPasswordMode && (
            <form onSubmit={handleSubmit} className={`auth-form ${isOTPMode ? 'otp-mode' : ''}`} noValidate>
            <div className="form-group">
              <label htmlFor="email">Email Address</label>
              <input
                ref={emailInputRef}
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onBlur={handleEmailBlur}
                required
                placeholder="Enter your email"
                className={getEmailInputClass()}
                disabled={isSubmitting || loading}
                autoComplete="email"
              />
              
              {/* Email validation feedback */}
              {email && (
                <div className="email-validation-feedback">
                  {shouldShowEmailError() && (
                    <div className="field-error">{emailValidation.error || 'Please enter a valid email address'}</div>
                  )}
                  
                  {emailValidation.suggestion && (
                    <div className="email-suggestion">
                      Did you mean{' '}
                      <button 
                        type="button"
                        className="suggestion-link"
                        onClick={handleEmailSuggestionClick}
                      >
                        {emailValidation.suggestion}
                      </button>?
                    </div>
                  )}
                </div>
              )}
            </div>
            
            <div className="form-group">
              <label htmlFor="password">
                {isSignUp ? 'Create Password' : 'Password'}
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  if (isSignUp && !showPasswordStrength && e.target.value.length > 0) {
                    setShowPasswordStrength(true);
                  }
                }}
                required
                placeholder={isSignUp ? 'Create a strong password' : 'Enter your password'}
                className="auth-input"
                disabled={isSubmitting || loading}
                autoComplete={isSignUp ? 'new-password' : 'current-password'}
              />
              
              {/* Password strength indicator for sign up */}
              {isSignUp && showPasswordStrength && (
                <PasswordStrengthIndicator password={password} />
              )}
            </div>
            
            {/* Confirm Password - only for sign up */}
            {isSignUp && (
              <div className="form-group">
                <label htmlFor="confirmPassword">Confirm Password</label>
                <input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  placeholder="Confirm your password"
                  className="auth-input"
                  disabled={isSubmitting || loading}
                  autoComplete="new-password"
                />
                
                {/* Password match indicator */}
                {confirmPassword && (
                  <div className={`password-match-indicator ${password === confirmPassword ? 'match' : 'no-match'}`}>
                    {password === confirmPassword ? (
                      <div className="match-success">
                        ✓ Passwords match
                      </div>
                    ) : (
                      <div className="match-error">
                        ✗ Passwords do not match
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
            
            <button 
              type="submit" 
              className={`auth-submit-button ${isSubmitting || loading ? 'loading' : ''}`}
              disabled={isSubmitting || loading}
            >
              {isSubmitting || loading ? (isSignUp ? 'Creating Account...' : 'Signing In...') : (isSignUp ? 'Create Account' : 'Sign In')}
            </button>
          </form>
          )}
          
          {/* Toggle and Forgot Password Links - Hidden during forgot password mode */}
          {!isForgotPasswordMode && (
            <>
              <div className={`auth-toggle ${isOTPMode ? 'otp-mode' : ''}`}>
                <span className="toggle-text">
                  {isSignUp ? 'Already have an account?' : 'New to ZoningPal?'}
                </span>
                <button 
                  onClick={toggleMode} 
                  className="toggle-button"
                  type="button"
                  disabled={isSubmitting || loading}
                >
                  {isSignUp ? 'Sign In' : 'Create Account'}
                </button>
              </div>
              
              {/* Forgot Password Link for Sign In */}
              {!isSignUp && !isOTPMode && (
                <div className="forgot-password-section">
                  <button 
                    type="button"
                    onClick={toggleForgotPasswordMode}
                    className="forgot-password-link-bottom"
                    disabled={isSubmitting || loading}
                  >
                    Forgot your password?
                  </button>
                </div>
              )}
            </>
          )}
          
          {isSignUp && (
            <div className={`auth-terms ${isOTPMode ? 'otp-mode' : ''}`}>
              <p>By creating an account, you agree to our Terms of Service and Privacy Policy.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LoginPage; 